import React, { useEffect, useRef } from "react";

const WA_NUMBER = "5491124289800";

const events = [
  {
    emoji: "🎂",
    title: "Cumpleaños infantiles",
    description: "Fiestas mágicas para los más chicos, con animación, juegos y todo lo necesario para un festejo único.",
    img: "/images/cumpleinfantil.jpeg",
    alt: "Cumpleaños infantil en salón de fiestas Quilmes",
    waMsg: "Hola! Quisiera consultar disponibilidad para un cumpleaños infantil.",
  },
  {
    emoji: "🎉",
    title: "Cumpleaños de adultos",
    description: "Celebrá tus años con estilo. Organizamos el festejo que siempre soñaste.",
    img: "/images/cumpleadultos.jpeg",
    alt: "Cumpleaños de adultos en salón de eventos Quilmes",
    waMsg: "Hola! Quisiera consultar para un cumpleaños de adultos.",
  },
  {
    emoji: "💙💗",
    title: "Revelación de género",
    description: "Un momento especial para revelar si es nene o nena, lleno de emoción y sorpresa.",
    img: "/images/revelaciongenero.jpeg",
    alt: "Revelación de género en Quilmes",
    waMsg: "Hola! Quisiera consultar para una revelación de género.",
  },
  {
    emoji: "👶",
    title: "Baby shower",
    description: "Celebrá la llegada del nuevo integrante de la familia con un festejo memorable.",
    img: "/images/babyshower.jpg",
    alt: "Baby shower en Quilmes Buenos Aires",
    waMsg: "Hola! Quisiera consultar para un baby shower.",
  },
  {
    emoji: "✝️",
    title: "Comunión",
    description: "Un día sagrado y especial merece una celebración a la altura, con elegancia y calidez.",
    img: "/images/comunion.jpg",
    alt: "Comunión en salón de fiestas Quilmes",
    waMsg: "Hola! Quisiera consultar para una comunión.",
  },
  {
    emoji: "🕊️",
    title: "Bautismo",
    description: "Comenzá este nuevo camino rodeado de familia y amigos en un espacio especial.",
    img: "/images/bautismo.jpg",
    alt: "Bautismo en salón de eventos Quilmes",
    waMsg: "Hola! Quisiera consultar para un bautismo.",
  },
  {
    emoji: "🥂",
    title: "Despedidas",
    description: "Despedidas de soltero/a y de año, con la energía y el ambiente que tu grupo merece.",
    img: "/images/cumpleadultos.jpg",
    alt: "Despedidas de soltera en Quilmes",
    waMsg: "Hola! Quisiera consultar para una despedida.",
  },
  {
    emoji: "🎓",
    title: "Egresados",
    description: "Celebrá tu egreso con una fiesta inolvidable junto a tus compañeros. Música, diversión y un espacio ideal para cerrar esta etapa como se merece.",
    img: "/images/egresados.jpg",
    alt: "Fiesta de egresados en salón de eventos Magic Kingdom en Quilmes",
    waMsg: "Hola! Quisiera consultar por una fiesta de egresados en Magic Kingdom.",
  }
];

export default function Events() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el) => {
              el.classList.add("visible");
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="eventos"
      ref={sectionRef}
      className="py-24 bg-background relative overflow-hidden"
      aria-labelledby="eventos-title"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 reveal">
          <p className="section-subtitle mb-3">Celebraciones</p>
          <h2 id="eventos-title" className="section-title mb-4">
            Eventos que realizamos
          </h2>
          <p className="font-body text-text/70 text-lg max-w-2xl mx-auto">
            Cada celebración es única. Nos adaptamos a cada ocasión para que vivas un momento especial.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {events.map((event, i) => (
            <article
              key={i}
              className="reveal group relative rounded-2xl overflow-hidden shadow-card card-hover cursor-pointer"
              style={{ transitionDelay: `${i * 60}ms` }}
              aria-labelledby={`event-${i}-title`}
            >
              {/* Image */}
              <div className="aspect-[3/4] relative">
                <img
                  src={event.img}
                  alt={event.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                {/* Gradient */}
                <div className="absolute inset-0 bg-card-gradient" />

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="text-2xl mb-2 block" aria-hidden="true">
                    {event.emoji}
                  </span>
                  <h3
                    id={`event-${i}-title`}
                    className="font-display text-white text-lg font-bold mb-1 text-shadow-sm"
                  >
                    {event.title}
                  </h3>
                  <p className="font-body text-white/80 text-xs leading-relaxed mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {event.description}
                  </p>
                  <a
                    href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(event.waMsg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 bg-white/20 hover:bg-gold backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
                    aria-label={`Consultar por ${event.title}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Consultar
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
